import { DiscoveryApi, FetchApi } from '@backstage/frontend-plugin-api';
import { ResponseError } from '@backstage/errors';
import type {
  ReportGenerationJob,
  ComplianceStatus,
  GlobalSyncStatus,
  Repository,
  Soc2ReportApi,
  SyncStatus,
  SyncRequest,
  UnapprovedPullRequest,
} from './types';

export class Soc2ReportClient implements Soc2ReportApi {
  private readonly discoveryApi: DiscoveryApi;
  private readonly fetchApi: FetchApi;

  constructor(options: { discoveryApi: DiscoveryApi; fetchApi: FetchApi }) {
    this.discoveryApi = options.discoveryApi;
    this.fetchApi = options.fetchApi;
  }

  private async baseUrl(): Promise<string> {
    return this.discoveryApi.getBaseUrl('soc2-report');
  }

  private async fetch<T>(path: string, init?: RequestInit): Promise<T> {
    const url = `${await this.baseUrl()}${path}`;
    const res = await this.fetchApi.fetch(url, init);
    if (!res.ok) {
      throw await ResponseError.fromResponse(res);
    }
    return res.json() as Promise<T>;
  }

  async getRepositories(): Promise<{
    repositories: Repository[];
    count: number;
  }> {
    return this.fetch('/repositories');
  }

  async getUnapprovedPRs(since?: string): Promise<{
    pullRequests: UnapprovedPullRequest[];
    count: number;
  }> {
    const params = since ? `?${new URLSearchParams({ since }).toString()}` : '';
    return this.fetch(`/unapproved${params}`);
  }

  async getSyncStatus(): Promise<{
    syncStatus: SyncStatus[];
    complianceStatus: ComplianceStatus[];
    globalSyncStatus: GlobalSyncStatus;
  }> {
    return this.fetch('/reports/status');
  }

  async triggerSync(): Promise<SyncRequest> {
    return this.fetch('/reports/sync', { method: 'POST' });
  }

  async generateReport(from: string, to: string): Promise<ReportGenerationJob> {
    const params = new URLSearchParams({ from, to }).toString();
    return this.fetch(`/reports/generate?${params}`, {
      method: 'POST',
    });
  }

  async getReportJob(id: string): Promise<ReportGenerationJob> {
    return this.fetch(`/reports/generate/${encodeURIComponent(id)}`);
  }

  async downloadCsv(type: string, from: string, to: string): Promise<void> {
    const params = new URLSearchParams({ from, to }).toString();
    const url = `${await this.baseUrl()}/reports/csv/${type}?${params}`;
    const res = await this.fetchApi.fetch(url);
    if (!res.ok) {
      throw await ResponseError.fromResponse(res);
    }
    const blob = await res.blob();
    const downloadUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = downloadUrl;
    a.download = `soc2-${type}-${from}-${to}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(downloadUrl);
  }
}
